<template>
  <div>
    <merus-data-table
      :headers="headers"
      :items="calculos"
      :loading="loading"
      :loading-text="$t('system.messages.loading')"
      :default-multi-sort="defaultSort"
      @update:displayedItems="displayedCalculos = $event"
    >
      <template v-slot:toolbar>
        <cadastro-toolbar
          :title="$t('subsidyMotorCatalog.calculations.title')"
          :token="token"
          :items="displayedCalculos !== null ? displayedCalculos : calculos"
          :csv-title="$t('subsidyMotorCatalog.calculations.listTitle')"
          :csv-headers="headers"
          hide-search
        >
          <template v-slot:add v-if="canInsert">
            <v-btn color="primary" dark small class="mb-2" fab @click="openNewCalculo">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
        </cadastro-toolbar>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon color="green" dark small @click="editCalculo(item)" :disabled="!canEdit">mdi-pencil</v-icon>
        <v-icon color="red" dark small @click="deleteCalculo(item)" :disabled="!canDelete">mdi-delete</v-icon>
      </template>
    </merus-data-table>

    <!-- Dialog Cálculo (fluxograma visual) -->
    <v-dialog v-model="dialogCalculo" max-width="1200px" persistent scrollable>
      <v-card class="calculo-dialog-card d-flex flex-column">
        <v-card-title class="calculo-dialog-title flex-shrink-0">{{ dialogCalculoTitle }}</v-card-title>
        <v-card-text class="calculo-dialog-body flex-grow-1">
          <v-form ref="formCalculo" v-model="formCalculoValidado">
            <v-row>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="editedCalculo.codigo"
                  :label="$t('subsidyMotorCatalog.fields.code')"
                  :rules="[requiredRule]"
                  :disabled="editedCalculoIndex !== -1"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="editedCalculo.finalidade"
                  :items="finalidadeOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('subsidyMotorCatalog.fields.purpose')"
                  :rules="[requiredRule]"
                  hide-details="auto"
                  @change="onFinalidadeCalculoChange"
                />
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="editedCalculo.tipo_resultado"
                  :items="tipoResultadoOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('subsidyMotorCatalog.fields.resultType')"
                  :rules="[requiredRule]"
                  :disabled="tipoResultadoTravado"
                  :hint="tipoResultadoTravado ? $t('subsidyMotorCatalog.fields.resultTypeLockedHint') : ''"
                  :persistent-hint="tipoResultadoTravado"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="editedCalculo.id_agrupamento"
                  :items="agrupamentos"
                  item-text="codigo"
                  item-value="id_agrupamento"
                  :label="$t('subsidyMotorCatalog.fields.baseGrouping')"
                  :rules="[requiredRule]"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editedCalculo.descricao"
                  :label="$t('system.description')"
                  :rules="[requiredRule]"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="4" v-if="isCalculoGrandeza">
                <v-select
                  v-model="editedCalculo.agregacao_resultado"
                  :items="agregacaoResultadoOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('subsidyMotorCatalog.fields.resultAggregation')"
                  :hint="$t('subsidyMotorCatalog.fields.resultAggregationHint')"
                  persistent-hint
                  :rules="[requiredRule]"
                />
              </v-col>
              <v-col cols="12" v-if="isCalculoValorContrato">
                <v-autocomplete
                  v-model="editedCalculo.id_tarifas"
                  :items="tarifas"
                  item-value="id_tarifa"
                  :item-text="tarifaLabel"
                  :label="$t('subsidyMotorCatalog.fields.linkedTariffs')"
                  :hint="$t('subsidyMotorCatalog.fields.linkedTariffsHint')"
                  persistent-hint
                  multiple
                  chips
                  deletable-chips
                  outlined
                  dense
                  clearable
                />
              </v-col>
            </v-row>

            <div class="calculo-flow-section mt-4">
              <div class="text-subtitle-2 font-weight-medium mb-2">
                {{ $t("subsidyMotorCatalog.fields.steps") }}
              </div>
              <calculo-flow-editor
                v-model="editedCalculo.passos"
                :variaveis-grandeza="variaveisGrandeza"
                :agrupamentos="agrupamentos"
                :funcao-agregacao-options="funcaoAgregacaoOptions"
                :default-agrupamento-id="editedCalculo.id_agrupamento"
                :variaveis-contrato="variaveisContrato"
                :output-label="editedCalculo.codigo || 'resultado'"
                :tipo-passo-options="tipoPassoOptions"
                :operador-comparacao-options="operadorComparacaoOptions"
                :operador-aritmetico-options="operadorAritmeticoOptions"
                :operador-logico-options="operadorLogicoOptions"
                :alias-rule="aliasRule"
              />
            </div>
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions class="calculo-dialog-actions flex-shrink-0">
          <v-spacer />
          <v-btn color="primary" dark @click="saveCalculo">{{ $t("button.save") }}</v-btn>
          <v-btn text @click="closeCalculo">{{ $t("button.cancel") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import MerusDataTable from "../../Tables/MerusDataTable.vue";
import CadastroToolbar from "../../Toolbars/CadastroToolbar.vue";
import CalculoFlowEditor from "../calculoFlow/CalculoFlowEditor.vue";
import apiController from "../../../utils/apiController";
import {
  isFinalidadeValor,
  isFinalidadeGrandeza,
} from "../../../utils/subsidioFinalidadeCalculo";
import EventBus from "../../../utils/eventBus";
import { funcaoAgregacaoOptions } from "../../../utils/subsidioFuncaoAgregacao";

const emptyPassoCalculo = () => ({
  _uid: `passo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  tipo_passo: "OBTER_GRANDEZA",
  config: { grandeza: { id_variavel: null, funcao_agregacao: "SOMA", id_agrupamento: null } },
  saida_alias: "",
  ordem: 0,
});

const emptyCalculo = () => ({
  id_calculo: null,
  codigo: "",
  descricao: "",
  tipo_resultado: "BOOLEANO",
  finalidade: "GERAL",
  agregacao_resultado: null,
  id_agrupamento: null,
  passos: [emptyPassoCalculo()],
  id_tarifas: [],
});

export default {
  components: {
    MerusDataTable,
    CadastroToolbar,
    CalculoFlowEditor,
  },
  props: {
    calculos: { type: Array, default: () => [] },
    agrupamentos: { type: Array, default: () => [] },
    variaveis: { type: Array, default: () => [] },
    tarifas: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    token: { type: String, default: null },
    canInsert: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    defaultSort: { type: Array, default: () => [] },
  },
  data: () => ({
    displayedCalculos: null,
    dialogCalculo: false,
    formCalculoValidado: true,
    editedCalculoIndex: -1,
    editedCalculo: emptyCalculo(),
  }),
  computed: {
    headers() {
      return [
        { text: this.$t("subsidyMotorCatalog.fields.code"), value: "codigo", sortable: true, filtrable: true },
        { text: this.$t("system.description"), value: "descricao", sortable: true, filtrable: true },
        { text: this.$t("subsidyMotorCatalog.fields.resultType"), value: "tipo_resultado", sortable: true, filtrable: true },
        { text: this.$t("subsidyMotorCatalog.fields.purpose"), value: "finalidadeLabel", sortable: true, filtrable: true },
        { text: this.$t("subsidyMotorCatalog.fields.linkedTariffs"), value: "tarifasLabel", sortable: false, filtrable: true },
        { text: this.$t("subsidyMotorCatalog.fields.baseGrouping"), value: "agrupamento_codigo", sortable: true, filtrable: true },
        { text: this.$t("subsidyMotorCatalog.fields.steps"), value: "passosLabel", sortable: false, filtrable: true },
        { text: this.$t("contract.table.headers.actions"), value: "actions", sortable: false, filtrable: false },
      ];
    },
    tipoResultadoOptions() {
      return [
        { value: "BOOLEANO", text: this.$t("subsidyMotorCatalog.resultTypes.boolean") },
        { value: "NUMERICO", text: this.$t("subsidyMotorCatalog.resultTypes.numeric") },
      ];
    },
    finalidadeOptions() {
      return [
        { value: "GERAL", text: this.$t("subsidyMotorCatalog.purposes.general") },
        { value: "VALOR_CONTRATO", text: this.$t("subsidyMotorCatalog.purposes.contractValue") },
        // Sinônimo já cadastrado em bases existentes; some da lista quando
        // nenhum cálculo o usa, para não oferecer dois nomes para a mesma coisa.
        ...(this.calculos || []).some((c) => c.finalidade === "MULTIPLICADOR_VALOR")
          ? [{ value: "MULTIPLICADOR_VALOR", text: this.$t("subsidyMotorCatalog.purposes.valueMultiplier") }]
          : [],
        { value: "GRANDEZA", text: this.$t("subsidyMotorCatalog.purposes.magnitude") },
      ];
    },
    agregacaoResultadoOptions() {
      return [
        { value: "SOMA", text: this.$t("subsidyMotorCatalog.resultAggregations.sum") },
        { value: "MEDIA", text: this.$t("subsidyMotorCatalog.resultAggregations.avg") },
        { value: "MEDIA_NAO_ZERO", text: this.$t("subsidyMotorCatalog.resultAggregations.avgNonZero") },
      ];
    },
    tipoPassoOptions() {
      return [
        { value: "OBTER_GRANDEZA", text: this.$t("subsidyMotorCatalog.stepTypes.obtainMagnitude") },
        { value: "OBTER_VARIAVEL_CONTRATO", text: this.$t("subsidyMotorCatalog.stepTypes.obtainContractVariable") },
        { value: "OBTER_PARAMETRO", text: this.$t("subsidyMotorCatalog.stepTypes.obtainConstant") },
        { value: "COMPARAR", text: this.$t("subsidyMotorCatalog.stepTypes.compare") },
        { value: "ARITMETICO", text: this.$t("subsidyMotorCatalog.stepTypes.arithmetic") },
        { value: "LOGICO", text: this.$t("subsidyMotorCatalog.stepTypes.logical") },
      ];
    },
    operadorComparacaoOptions() {
      return [
        { value: "GT", text: ">" },
        { value: "GTE", text: ">=" },
        { value: "LT", text: "<" },
        { value: "LTE", text: "<=" },
        { value: "EQ", text: "=" },
        { value: "NEQ", text: "!=" },
      ];
    },
    operadorAritmeticoOptions() {
      return [
        { value: "DIV", text: "/" },
        { value: "MUL", text: "*" },
        { value: "ADD", text: "+" },
        { value: "SUB", text: "-" },
      ];
    },
    operadorLogicoOptions() {
      return [
        {
          value: "AND",
          text: this.$te("subsidyMotorCatalog.logicalOperators.and")
            ? this.$t("subsidyMotorCatalog.logicalOperators.and")
            : "Todas verdadeiras",
        },
        {
          value: "OR",
          text: this.$te("subsidyMotorCatalog.logicalOperators.or")
            ? this.$t("subsidyMotorCatalog.logicalOperators.or")
            : "Ao menos um verdadeiro",
        },
        {
          value: "NOT",
          text: this.$te("subsidyMotorCatalog.logicalOperators.not")
            ? this.$t("subsidyMotorCatalog.logicalOperators.not")
            : "Inverter resultado",
        },
      ];
    },
    isCalculoValorContrato() {
      return isFinalidadeValor(this.editedCalculo.finalidade);
    },
    isCalculoGrandeza() {
      return isFinalidadeGrandeza(this.editedCalculo.finalidade);
    },
    // Valor de contrato e grandeza são sempre numéricos.
    tipoResultadoTravado() {
      return this.isCalculoValorContrato || this.isCalculoGrandeza;
    },
    dialogCalculoTitle() {
      return this.editedCalculoIndex === -1
        ? this.$t("subsidyMotorCatalog.calculations.new")
        : `${this.$t("button.edit")} — ${this.$t("subsidyMotorCatalog.tabs.calculations")}`;
    },
    requiredRule() {
      return (v) => (!!v && String(v).trim().length > 0) || this.$t("system.messages.requiredField");
    },
    aliasRule() {
      return (v) => /^[a-z][a-z0-9_]*$/.test(String(v || "").trim()) || this.$t("subsidyMotorCatalog.validation.operandAlias");
    },
    variaveisContrato() {
      return (this.variaveis || []).filter((v) => v.fonte === "contrato");
    },
    variaveisGrandeza() {
      return (this.variaveis || []).filter((v) => v.fonte !== "contrato");
    },
    funcaoAgregacaoOptions() {
      return funcaoAgregacaoOptions(this.$t.bind(this));
    },
  },
  methods: {
    tarifaLabel(t) {
      if (!t) return "";
      const categoria = t.cod_categoria ? ` (${t.cod_categoria})` : "";
      return `${t.cod_tarifa || t.id_tarifa} — ${t.descricao_tarifa || ""}${categoria}`;
    },
    onFinalidadeCalculoChange() {
      if (this.tipoResultadoTravado) {
        // Valor de contrato e grandeza são sempre numéricos; a regra também é
        // validada no servidor.
        this.editedCalculo.tipo_resultado = "NUMERICO";
      }
      if (!isFinalidadeValor(this.editedCalculo.finalidade)) {
        this.editedCalculo.id_tarifas = [];
      }
      this.editedCalculo.agregacao_resultado = this.isCalculoGrandeza
        ? this.editedCalculo.agregacao_resultado || "SOMA"
        : null;
    },
    openNewCalculo() {
      this.editedCalculoIndex = -1;
      this.editedCalculo = emptyCalculo();
      this.dialogCalculo = true;
    },
    editCalculo(item) {
      this.editedCalculoIndex = this.calculos.indexOf(item);
      this.editedCalculo = {
        id_calculo: item.id_calculo,
        codigo: item.codigo,
        descricao: item.descricao,
        tipo_resultado: item.tipo_resultado,
        finalidade: item.finalidade || "GERAL",
        agregacao_resultado: item.agregacao_resultado || null,
        id_agrupamento: item.id_agrupamento,
        id_tarifas: Array.isArray(item.id_tarifas) ? item.id_tarifas.slice() : [],
        passos: (item.passos || []).map((p, idx) => ({
          _uid: `passo-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`,
          tipo_passo: p.tipo_passo,
          config: JSON.parse(JSON.stringify(p.config || {})),
          saida_alias: p.saida_alias,
          ordem: p.ordem != null ? p.ordem : idx,
        })),
      };
      if (!this.editedCalculo.passos.length) {
        this.editedCalculo.passos = [emptyPassoCalculo()];
      }
      this.dialogCalculo = true;
    },
    closeCalculo() {
      this.dialogCalculo = false;
      this.editedCalculo = emptyCalculo();
      this.editedCalculoIndex = -1;
      if (this.$refs.formCalculo) this.$refs.formCalculo.resetValidation();
    },
    buildCalculoPayload() {
      return {
        id_calculo: this.editedCalculo.id_calculo,
        codigo: this.editedCalculo.codigo,
        descricao: this.editedCalculo.descricao,
        tipo_resultado: this.editedCalculo.tipo_resultado,
        finalidade: this.editedCalculo.finalidade,
        agregacao_resultado: this.editedCalculo.agregacao_resultado || null,
        id_agrupamento: this.editedCalculo.id_agrupamento,
        id_tarifas: this.editedCalculo.id_tarifas || [],
        passos: (this.editedCalculo.passos || []).map((p, idx) => ({
          ordem: idx,
          tipo_passo: p.tipo_passo,
          config: p.config || {},
          saida_alias: String(p.saida_alias || "").trim().toLowerCase(),
        })),
      };
    },
    // O editor de fluxo não participa do v-form; a definição de cada grandeza é
    // conferida aqui para o erro aparecer antes de ir ao servidor.
    passoGrandezaIncompleto() {
      return (this.editedCalculo.passos || []).find((p) => {
        if (p.tipo_passo !== "OBTER_GRANDEZA") return false;
        const def = p.config && p.config.grandeza;
        return !def || !def.id_variavel || !def.funcao_agregacao || !def.id_agrupamento;
      });
    },
    async saveCalculo() {
      if (this.$refs.formCalculo) this.$refs.formCalculo.validate();
      if (!this.formCalculoValidado) return;
      const incompleto = this.passoGrandezaIncompleto();
      if (incompleto) {
        EventBus.$emit("show-alert", {
          mensagem: this.$t("subsidyMotorCatalog.validation.magnitudeIncomplete", {
            passo: incompleto.saida_alias || "?",
          }),
          tipo: "error",
        });
        return;
      }
      const endpoint =
        this.editedCalculoIndex === -1
          ? apiController.endpoints.subsidioMotorInsertCalculo
          : apiController.endpoints.subsidioMotorUpdateCalculo;
      try {
        const resp = await apiController.request(endpoint, { data: this.buildCalculoPayload(), silent: true });
        if (resp.data && resp.data.erro) {
          EventBus.$emit("show-alert", { mensagem: resp.data.mensagem || this.$t("system.unknownSystemError"), tipo: "error" });
          return;
        }
        EventBus.$emit("show-alert", { mensagem: this.$t("subsidyMotorCatalog.saved"), tipo: "success" });
        this.closeCalculo();
        this.$emit("refresh", ["calculos"]);
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
    async deleteCalculo(item) {
      try {
        await apiController.request(apiController.endpoints.subsidioMotorDeleteCalculo, {
          data: { id_calculo: item.id_calculo },
          silent: true,
        });
        this.$emit("refresh", ["calculos"]);
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
  },
};
</script>

<style scoped>
.calculo-dialog-card {
  max-height: 90vh;
  overflow: hidden;
}

.calculo-dialog-body {
  overflow-y: auto;
  min-height: 0;
  padding-bottom: 8px;
}

.calculo-dialog-title {
  padding-bottom: 8px;
}

.calculo-dialog-actions {
  background: #fff;
}

.calculo-flow-section {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 16px;
  padding-bottom: 4px;
}
</style>
